import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { isNotNullish } from '@bcef/ia-shared';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { FormStep, RoutesPath } from '@features/succession/enums';
import { DeceasedMoreInformationForm } from '@features/succession/forms';
import { DeceasedMoreInformationFormValues } from '@features/succession/interfaces';
import {
  CustomerInformation,
  DeceasedMoreInformation,
  DocumentAttachmentGroup,
  DocumentItem,
  TypeDocument,
} from '@features/succession/models';
import * as fromStore from '@features/succession/store';
import { SuccessionFacade } from '@features/succession/succession.facade';

@Injectable({ providedIn: 'root' })
export class DeceasedMoreInformationFacade {
  protected destroyRef = inject(DestroyRef);

  private readonly successionFacade = inject(SuccessionFacade);

  private readonly formBuilder = inject(FormBuilder);

  private readonly store = inject(Store);

  public deceasedMoreInformationForm: FormGroup<DeceasedMoreInformationForm> =
    this.formBuilder.group<DeceasedMoreInformationForm>({
      deceasedDate: new FormControl(null, [Validators.required]),
      hasFuneralContract: new FormControl('', [Validators.required]),
      deathCertificateDocuments: new FormControl([], [Validators.required]),
      otherDocuments: new FormControl([]),
      funeralHomeBillsDocuments: new FormControl([]),
      ribDocuments: new FormControl([]),
      funeralHomePaymentAuthorizationDocuments: new FormControl([]),
    });

  public deathCertificateAttachmentGroup$: Observable<DocumentAttachmentGroup> = this.store
    .select(fromStore.selectDeathCertificateAttachmentGroup)
    .pipe(isNotNullish());

  public deathCertificateTypeDocuments$: Observable<TypeDocument[]> = this.store
    .select(fromStore.selectDeathCertificateTypeDocuments)
    .pipe(isNotNullish());

  public otherAttachmentGroup$: Observable<DocumentAttachmentGroup> = this.store
    .select(fromStore.selectOtherAttachmentGroup)
    .pipe(isNotNullish());

  public otherSelfCareTypeDocuments$: Observable<TypeDocument[]> = this.store
    .select(fromStore.selectOtherSelfCareTypeDocuments)
    .pipe(isNotNullish());

  public funeralHomeBillsAttachmentGroup$: Observable<DocumentAttachmentGroup> = this.store
    .select(fromStore.selectFuneralHomeBillsAttachmentGroup)
    .pipe(isNotNullish());

  public funeralHomeBillsTypeDocuments$: Observable<TypeDocument[]> = this.store
    .select(fromStore.selectFuneralHomeBillsTypeDocuments)
    .pipe(isNotNullish());

  public ribAttachmentGroup$: Observable<DocumentAttachmentGroup> = this.store
    .select(fromStore.selectRibAttachmentGroup)
    .pipe(isNotNullish());

  public ribTypeDocuments$: Observable<TypeDocument[]> = this.store
    .select(fromStore.selectRibTypeDocuments)
    .pipe(isNotNullish());

  public funeralHomePaymentAuthorizationAttachmentGroup$: Observable<DocumentAttachmentGroup> =
    this.store
      .select(fromStore.selectFuneralHomePaymentAuthorizationAttachmentGroup)
      .pipe(isNotNullish());

  public funeralHomePaymentAuthorizationTypeDocuments$: Observable<TypeDocument[]> = this.store
    .select(fromStore.selectFuneralHomePaymentAuthorizationTypeDocuments)
    .pipe(isNotNullish());

  constructor() {
    this.successionFacade.deceasedMoreInformationFormValues$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deceasedMoreInformationFormValues: DeceasedMoreInformationFormValues) => {
        this.deceasedMoreInformationForm.patchValue(deceasedMoreInformationFormValues);
      });
  }

  public get deceasedDate(): FormControl<Date | null> {
    return this.deceasedMoreInformationForm.controls.deceasedDate;
  }

  public get hasFuneralContract(): FormControl<string | null> {
    return this.deceasedMoreInformationForm.controls.hasFuneralContract;
  }

  public get deathCertificateDocuments(): FormControl<DocumentItem[] | null> {
    return this.deceasedMoreInformationForm.controls.deathCertificateDocuments;
  }

  public get otherDocuments(): FormControl<DocumentItem[] | null> {
    return this.deceasedMoreInformationForm.controls.otherDocuments;
  }

  public get funeralHomeBillsDocuments(): FormControl<DocumentItem[] | null> {
    return this.deceasedMoreInformationForm.controls.funeralHomeBillsDocuments;
  }

  public get ribDocuments(): FormControl<DocumentItem[] | null> {
    return this.deceasedMoreInformationForm.controls.ribDocuments;
  }

  public get funeralHomePaymentAuthorizationDocuments(): FormControl<DocumentItem[] | null> {
    return this.deceasedMoreInformationForm.controls.funeralHomePaymentAuthorizationDocuments;
  }

  private navigateToPage(route: RoutesPath): void {
    const deceasedMoreInformation: DeceasedMoreInformation = {
      deceasedDate: this.deceasedDate.value || undefined,
      hasFuneralContract: this.hasFuneralContract.value || '',
      documents: [
        ...(this.deathCertificateDocuments.value || []),
        ...(this.otherDocuments.value || []),
        ...(this.funeralHomeBillsDocuments.value || []),
        ...(this.ribDocuments.value || []),
        ...(this.funeralHomePaymentAuthorizationDocuments.value || []),
      ],
    };

    const customerInformation: CustomerInformation = {
      token: this.successionFacade.token,
      savePage: FormStep.TWO,
      loadPage: route === RoutesPath.PAGE_1 ? FormStep.ONE : FormStep.THREE,
      deceasedMoreInformations: deceasedMoreInformation,
      deceasedInformations: undefined,
      reporterInformations: undefined,
      notaryInformations: undefined,
      inheritanceInformations: undefined,
    };

    this.successionFacade.saveAndLoad(customerInformation);
  }

  public onPrevious(): void {
    this.navigateToPage(RoutesPath.PAGE_1);
    this.successionFacade.trackPrevious();
  }

  public onNext(): void {
    this.deceasedMoreInformationForm.markAllAsTouched();
    if (this.deceasedMoreInformationForm.invalid) {
      return;
    }

    this.navigateToPage(RoutesPath.PAGE_3);
    this.successionFacade.trackNext();
  }
}
